<template>
  <div class="admin-shell" @click="onShellClick">
    <!-- Shell header -->
    <nav class="admin-nav">
      <div class="admin-nav-left">
        <div class="admin-nav-logo">
          <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="10" fill="rgba(255,255,255,0.15)"/>
            <path d="M24 10L36 18V30L24 38L12 30V18L24 10Z" fill="white" fill-opacity="0.2" stroke="white" stroke-width="2"/>
            <circle cx="24" cy="24" r="6" fill="white"/>
          </svg>
        </div>
        <span class="admin-nav-product">Obra Tycoon</span>
        <span class="admin-nav-divider"></span>
        <span class="admin-nav-title">Admin</span>
      </div>
      <div class="admin-nav-right">
        <!-- Bell -->
        <div class="bell-wrap" @click.stop="toggleBell">
          <button class="admin-nav-bell" type="button" aria-label="Notificacoes">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
            <span v-if="pendingCount > 0" class="bell-badge">{{ pendingCount }}</span>
          </button>
          <!-- Bell dropdown -->
          <div v-if="bellOpen" class="bell-dropdown" @click.stop>
            <div class="bell-dropdown-header">Solicitações de Reativação</div>
            <div v-if="reactivationRequests.length === 0" class="bell-empty">Nenhuma solicitação pendente</div>
            <div v-for="req in reactivationRequests" :key="req.id" class="bell-item">
              <div class="bell-item-title">{{ req.siteName || req.systemId }}</div>
              <div class="bell-item-meta">Por: {{ req.requestedByUsername }} &middot; {{ formatDate(req.requestedAt) }}</div>
              <div v-if="req.message" class="bell-item-message">{{ req.message }}</div>
              <button class="bell-item-btn" type="button" @click="viewSystem(req)">Ver obra</button>
            </div>
          </div>
        </div>
        <!-- User avatar -->
        <div class="admin-nav-user-group">
          <div class="admin-nav-avatar">{{ (masterSessionStore.state.admin?.name || 'A')[0].toUpperCase() }}</div>
          <span class="admin-nav-username">{{ masterSessionStore.state.admin?.name }}</span>
        </div>
        <button class="admin-nav-logout" type="button" title="Verificação em duas etapas" @click="show2fa = true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 11 12 14 16 9" transform="scale(0.7) translate(5.2 5.2)"/></svg>
          2FA
        </button>
        <button class="admin-nav-logout" type="button" title="Trocar senha" @click="openChangePwd">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          Senha
        </button>
        <button class="admin-nav-logout" type="button" @click="logout">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Sair
        </button>
      </div>
    </nav>

    <!-- Modal: trocar senha do master -->
    <Teleport to="body">
      <div v-if="changePwd.open" class="cpw-overlay" @click.self="closeChangePwd">
        <div class="cpw-modal">
          <h3 class="cpw-title">Trocar minha senha</h3>
          <p class="cpw-sub">Senha do administrador geral. Mínimo 8 caracteres.</p>
          <div class="cpw-field">
            <label>Senha atual</label>
            <input v-model="changePwd.current" type="password" autocomplete="current-password" />
          </div>
          <div class="cpw-field">
            <label>Nova senha</label>
            <input v-model="changePwd.next" type="password" autocomplete="new-password" />
          </div>
          <div class="cpw-field">
            <label>Confirmar nova senha</label>
            <input v-model="changePwd.confirm" type="password" autocomplete="new-password" @keyup.enter="submitChangePwd" />
          </div>
          <p v-if="changePwd.error" class="cpw-error">{{ changePwd.error }}</p>
          <p v-if="changePwd.success" class="cpw-success">{{ changePwd.success }}</p>
          <div class="cpw-actions">
            <button class="cpw-btn cpw-btn--ghost" type="button" @click="closeChangePwd">Cancelar</button>
            <button class="cpw-btn cpw-btn--primary" type="button" :disabled="changePwd.saving" @click="submitChangePwd">
              {{ changePwd.saving ? 'Salvando...' : 'Alterar senha' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal: 2FA -->
    <Admin2FAModal v-if="show2fa" @close="show2fa = false" @changed="() => {}" />

    <!-- Command palette (Ctrl+K) -->
    <Teleport to="body">
      <div v-if="paletteOpen" class="pal-overlay" @click.self="closePalette">
        <div class="pal-box">
          <div class="pal-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input ref="paletteInput" v-model="paletteQuery" type="text" placeholder="Ir para obra... (nome ou @username)"
              @keydown.enter="paletteResults[0] && paletteGo(paletteResults[0].systemId)" />
            <kbd class="pal-kbd">Esc</kbd>
          </div>
          <div class="pal-results">
            <button v-for="s in paletteResults" :key="s.systemId" class="pal-item" type="button" @click="paletteGo(s.systemId)">
              <span class="pal-dot" :style="{ background: s.primaryColor || '#0f4c81' }" />
              <span class="pal-name">{{ s.siteName }}</span>
              <span class="pal-user">@{{ s.systemUsername }}</span>
              <span v-if="s.disabled" class="pal-badge">suspensa</span>
            </button>
            <div v-if="paletteResults.length === 0" class="pal-empty">Nenhuma obra encontrada.</div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Faixa de KPIs (visão executiva) -->
    <div class="admin-kpis">
      <div class="kpi">
        <span class="kpi-value">{{ kpis.total }}</span>
        <span class="kpi-label">Obras</span>
      </div>
      <div class="kpi">
        <span class="kpi-value kpi-value--ok">{{ kpis.ativas }}</span>
        <span class="kpi-label">Ativas</span>
      </div>
      <div class="kpi">
        <span class="kpi-value" :class="kpis.suspensas > 0 ? 'kpi-value--warn' : ''">{{ kpis.suspensas }}</span>
        <span class="kpi-label">Suspensas</span>
      </div>
      <div class="kpi">
        <span class="kpi-value">{{ kpis.usuarios }}</span>
        <span class="kpi-label">Usuários</span>
      </div>
      <div class="kpi">
        <span class="kpi-value">{{ kpis.projetos }}</span>
        <span class="kpi-label">Projetos ativos</span>
      </div>
      <div class="kpi kpi--storage">
        <div class="kpi-storage-top">
          <span class="kpi-value">{{ kpis.usedLabel }}</span>
          <span class="kpi-storage-of">de {{ kpis.limitLabel }}</span>
        </div>
        <div class="kpi-storage-bar"><div class="kpi-storage-fill" :style="{ width: kpis.storagePct + '%', background: kpis.storagePct >= 90 ? '#dc2626' : kpis.storagePct >= 70 ? '#f59e0b' : '#16a34a' }" /></div>
        <span class="kpi-label">Armazenamento total</span>
      </div>
      <button class="kpi-cmdk" type="button" @click="openPalette" title="Buscar obra (Ctrl+K)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        Buscar <kbd>Ctrl K</kbd>
      </button>
    </div>

    <!-- Command bar: pivot tabs + actions -->
    <div class="admin-cmdbar">
      <div class="admin-pivot">
        <button class="pivot-tab" :class="{ active: dashTab === 'ativas' }" type="button" @click="dashTab = 'ativas'">Obras Ativas</button>
        <button class="pivot-tab" :class="{ active: dashTab === 'deletadas' }" type="button" @click="loadDeleted(); dashTab = 'deletadas'">Deletadas</button>
        <button class="pivot-tab" :class="{ active: dashTab === 'analytics' }" type="button" @click="loadAnalytics(); dashTab = 'analytics'">Analytics</button>
      </div>
      <div class="admin-cmdbar-actions">
        <button class="btn-cmd" type="button" :disabled="backupLoading" @click="downloadBackup">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {{ backupLoading ? 'Gerando...' : 'Backup' }}
        </button>
        <button v-if="dashTab === 'ativas'" class="btn-cmd" type="button" @click="showImportModal = true">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Importar
        </button>
        <button v-if="dashTab === 'ativas'" class="btn-cmd-primary" type="button" @click="showCreateModal = true">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Nova Obra
        </button>
      </div>
    </div>

    <!-- Content -->
    <main class="admin-main">
      <div class="admin-content-inner">

      <!-- ATIVAS -->
      <template v-if="dashTab === 'ativas'">
        <!-- Search -->
        <div class="admin-search-wrap">
          <input v-model="searchQuery" class="admin-search" type="text" placeholder="Buscar obra por nome ou username..." />
        </div>

        <div v-if="loading" class="admin-loading">Carregando obras...</div>

        <div v-else class="admin-grid">
        <div
          v-for="s in filteredSystems"
          :key="s.systemId"
          class="obra-card"
          :class="{ disabled: s.disabled }"
          @click="goToSystem(s.systemId)"
        >
          <div class="obra-card-header" :style="{ background: s.primaryColor || '#0f4c81' }">
            <span class="obra-card-name">{{ s.siteName }}</span>
            <span class="obra-card-badge" :class="s.disabled ? 'badge-off' : 'badge-on'">
              {{ s.disabled ? 'Inativo' : 'Ativo' }}
            </span>
          </div>

          <div class="obra-card-body">
            <div class="obra-stat-row">
              <div class="obra-stat">
                <span class="obra-stat-value">{{ s.activeUsers }}</span>
                <span class="obra-stat-label">Usuários ativos</span>
              </div>
              <div class="obra-stat">
                <span class="obra-stat-value">{{ s.inactiveUsers }}</span>
                <span class="obra-stat-label">Inativos</span>
              </div>
              <div class="obra-stat">
                <span class="obra-stat-value">{{ s.activeProjects }}</span>
                <span class="obra-stat-label">Projetos</span>
              </div>
            </div>

            <div class="obra-storage">
              <div class="obra-storage-bar-wrap">
                <div
                  class="obra-storage-bar-fill"
                  :style="{ width: Math.min(100, (s.usedMb / s.storageLimitMb) * 100) + '%', background: storageColor(s.usedMb, s.storageLimitMb) }"
                />
              </div>
              <span class="obra-storage-label">{{ formatMb(s.usedMb) }} / {{ formatMb(s.storageLimitMb) }}</span>
            </div>

            <div class="obra-dates">
              <div class="obra-date-item">
                <span class="obra-date-label">Início</span>
                <span class="obra-date-value">{{ formatDate(s.createdAt) }}</span>
              </div>
              <div class="obra-date-item">
                <span class="obra-date-label">Renovação</span>
                <span class="obra-date-value">{{ formatDate(s.renewalDate) || '—' }}</span>
              </div>
            </div>

            <div class="obra-card-actions" @click.stop>
              <button
                class="btn-obra-export"
                type="button"
                :disabled="exportLoading === s.systemId"
                @click.stop="exportObra(s)"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {{ exportLoading === s.systemId ? 'Exportando...' : 'Exportar ZIP' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      </template>

      <!-- ANALYTICS -->
      <template v-if="dashTab === 'analytics'">
        <!-- Analytics sub-tabs -->
        <div class="analytics-subtabs">
          <button class="asubtab" :class="{ active: analyticsSubTab === 'geral' }" type="button" @click="switchAnalyticsTab('geral')">
            Visão geral
          </button>
          <button class="asubtab" :class="{ active: analyticsSubTab === 'consumo' }" type="button" @click="switchAnalyticsTab('consumo')">
            Consumo
          </button>
        </div>

        <!-- GERAL -->
        <template v-if="analyticsSubTab === 'geral'">
          <div v-if="analyticsLoading" class="admin-loading">Carregando analytics...</div>
          <div v-else-if="analytics" class="analytics-wrap">
            <div class="analytics-cards">
              <div class="acard">
                <span class="acard-value">{{ analytics.totalSystems }}</span>
                <span class="acard-label">Obras totais</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ analytics.activeSystems }}</span>
                <span class="acard-label">Obras ativas</span>
              </div>
              <div class="acard acard-warn">
                <span class="acard-value">{{ analytics.suspendedSystems }}</span>
                <span class="acard-label">Suspensas</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ analytics.totalUsers }}</span>
                <span class="acard-label">Usuários</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ analytics.totalProjects }}</span>
                <span class="acard-label">Projetos</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ analytics.totalSubmissions }}</span>
                <span class="acard-label">Relatórios</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ formatMb(analytics.totalFileMb) }}</span>
                <span class="acard-label">Arquivos</span>
              </div>
            </div>
            <div class="analytics-cols">
              <div class="analytics-panel">
                <h3 class="analytics-panel-title">Ranking por usuários</h3>
                <div class="rank-list">
                  <div v-for="(s, i) in analytics.topByUsers" :key="s.systemId" class="rank-row" @click="goToSystem(s.systemId)">
                    <span class="rank-num">#{{ i + 1 }}</span>
                    <span class="rank-name">{{ s.siteName }}</span>
                    <span class="rank-val">{{ s.userCount }} usuários</span>
                  </div>
                </div>
              </div>
              <div class="analytics-panel">
                <h3 class="analytics-panel-title">Ranking por atividade (mês atual)</h3>
                <div class="rank-list">
                  <div v-for="(s, i) in analytics.topByActivity" :key="s.systemId" class="rank-row" @click="goToSystem(s.systemId)">
                    <span class="rank-num">#{{ i + 1 }}</span>
                    <span class="rank-name">{{ s.siteName }}</span>
                    <span class="rank-val">{{ s.submissionCount }} relatórios</span>
                  </div>
                </div>
              </div>
              <div class="analytics-panel">
                <h3 class="analytics-panel-title">Renovações nos próximos 30 dias</h3>
                <div v-if="analytics.expiringRenewals.length === 0" class="rank-empty">Nenhuma renovação nos próximos 30 dias.</div>
                <div class="rank-list">
                  <div v-for="s in analytics.expiringRenewals" :key="s.systemId" class="rank-row rank-row-warn" @click="goToSystem(s.systemId)">
                    <span class="rank-name">{{ s.siteName }}</span>
                    <span class="rank-val">{{ formatDate(s.renewalDate) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- CONSUMO -->
        <template v-else-if="analyticsSubTab === 'consumo'">
          <div v-if="storageLoading" class="admin-loading">Carregando dados de consumo...</div>
          <div v-else-if="storageOverview" class="storage-wrap">

            <!-- Global summary cards -->
            <div class="analytics-cards">
              <div class="acard">
                <span class="acard-value">{{ formatMb(storageOverview.totalUsedMb) }}</span>
                <span class="acard-label">Total utilizado</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ formatMb(storageOverview.totalLimitMb) }}</span>
                <span class="acard-label">Capacidade total</span>
              </div>
              <div class="acard" :class="storageOverview.totalPct >= 80 ? 'acard-danger' : storageOverview.totalPct >= 60 ? 'acard-warn' : ''">
                <span class="acard-value">{{ storageOverview.totalPct }}%</span>
                <span class="acard-label">Ocupação global</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ formatMb(storageOverview.filesMb) }}</span>
                <span class="acard-label">Arquivos de projeto</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ formatMb(storageOverview.submMb) }}</span>
                <span class="acard-label">Relatórios</span>
              </div>
              <div class="acard">
                <span class="acard-value">{{ formatMb(storageOverview.mediaMb) }}</span>
                <span class="acard-label">Mídias (fotos)</span>
              </div>
            </div>

            <!-- Global donut-style bar -->
            <div class="storage-global-bar-card">
              <div class="storage-global-title">Distribuição global do armazenamento</div>
              <div class="storage-legend">
                <span class="sleg sleg-files">Arquivos — {{ formatMb(storageOverview.filesMb) }}</span>
                <span class="sleg sleg-subm">Relatórios — {{ formatMb(storageOverview.submMb) }}</span>
                <span class="sleg sleg-media">Mídias — {{ formatMb(storageOverview.mediaMb) }}</span>
                <span class="sleg sleg-free">Livre — {{ formatMb(Math.max(0, storageOverview.totalLimitMb - storageOverview.totalUsedMb)) }}</span>
              </div>
              <div class="storage-stacked-bar">
                <div class="ssb-seg ssb-files" :style="{ width: (storageOverview.totalLimitMb > 0 ? storageOverview.filesMb / storageOverview.totalLimitMb * 100 : 0) + '%' }"></div>
                <div class="ssb-seg ssb-subm"  :style="{ width: (storageOverview.totalLimitMb > 0 ? storageOverview.submMb  / storageOverview.totalLimitMb * 100 : 0) + '%' }"></div>
                <div class="ssb-seg ssb-media" :style="{ width: (storageOverview.totalLimitMb > 0 ? storageOverview.mediaMb / storageOverview.totalLimitMb * 100 : 0) + '%' }"></div>
              </div>
            </div>

            <!-- Per-system table -->
            <div class="analytics-panel storage-table-panel">
              <div class="storage-table-header">
                <h3 class="analytics-panel-title" style="margin:0">Consumo por obra</h3>
                <span class="storage-table-note">{{ storageOverview.systems.length }} obras</span>
              </div>
              <div class="storage-table-wrap">
                <table class="storage-table">
                  <thead>
                    <tr>
                      <th class="th-sort" @click="toggleStorageSort('siteName')">
                        Obra
                        <span class="sort-arrow">{{ storageSortField === 'siteName' ? (storageSortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                      </th>
                      <th class="th-sort th-num" @click="toggleStorageSort('usedMb')">
                        Usado
                        <span class="sort-arrow">{{ storageSortField === 'usedMb' ? (storageSortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                      </th>
                      <th class="th-num">Limite</th>
                      <th class="th-sort th-num" @click="toggleStorageSort('pct')">
                        %
                        <span class="sort-arrow">{{ storageSortField === 'pct' ? (storageSortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                      </th>
                      <th>Distribuição</th>
                      <th class="th-num">Arquivos</th>
                      <th class="th-num">Relatórios</th>
                      <th class="th-num">Mídias</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="s in storageSorted" :key="s.systemId" class="storage-tr" @click="goToSystem(s.systemId)">
                      <td class="td-name">
                        <span class="td-site">{{ s.siteName }}</span>
                        <span class="td-user">@{{ s.systemUsername }}</span>
                      </td>
                      <td class="td-num">{{ formatMb(s.usedMb) }}</td>
                      <td class="td-num">{{ formatMb(s.limitMb) }}</td>
                      <td class="td-num">
                        <span class="pct-badge" :style="{ background: storageBarColor(s.pct) + '22', color: storageBarColor(s.pct) }">
                          {{ s.pct }}%
                        </span>
                      </td>
                      <td class="td-bar">
                        <div class="mini-stacked">
                          <div class="ssb-seg ssb-files" :style="{ width: (s.limitMb > 0 ? s.filesMb / s.limitMb * 100 : 0) + '%' }"></div>
                          <div class="ssb-seg ssb-subm"  :style="{ width: (s.limitMb > 0 ? s.submMb  / s.limitMb * 100 : 0) + '%' }"></div>
                          <div class="ssb-seg ssb-media" :style="{ width: (s.limitMb > 0 ? s.mediaMb / s.limitMb * 100 : 0) + '%' }"></div>
                        </div>
                      </td>
                      <td class="td-num td-muted">{{ formatMb(s.filesMb) }}</td>
                      <td class="td-num td-muted">{{ formatMb(s.submMb) }}</td>
                      <td class="td-num td-muted">{{ formatMb(s.mediaMb) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- DELETADAS -->
      <template v-if="dashTab === 'deletadas'">
        <div v-if="deletedLoading" class="admin-loading">Carregando...</div>
        <div v-else-if="deletedSystems.length === 0" class="admin-loading" style="color:#9ca3af">Nenhuma obra deletada.</div>
        <table v-else class="deleted-table">
          <thead>
            <tr>
              <th>Nome da obra</th>
              <th>Username</th>
              <th>Usuários</th>
              <th>Projetos</th>
              <th>Dados</th>
              <th>Deletada em</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in deletedSystems" :key="d.systemId">
              <td>{{ d.siteName }}</td>
              <td><code>{{ d.originalSystemUsername }}</code></td>
              <td>{{ d.totalUsers }}</td>
              <td>{{ d.totalProjects }}</td>
              <td>{{ formatMb(d.usedMb) }}</td>
              <td>{{ formatDate(d.deletedAt) }}</td>
              <td>
                <button class="btn-recover" type="button" :disabled="recoveringId === d.systemId" @click="recoverSystem(d)">
                  {{ recoveringId === d.systemId ? 'Recuperando...' : 'Recuperar obra' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
      </div><!-- /admin-content-inner -->
    </main>

    <!-- Import Modal -->
    <div v-if="showImportModal" class="modal-overlay" @click.self="closeImportModal">
      <div class="modal-box">
        <h2 class="modal-title">Importar obra</h2>
        <div class="modal-fields">
          <div class="modal-field">
            <label>Arquivo ZIP da obra</label>
            <input type="file" accept=".zip" @change="onImportFile" />
          </div>
          <div class="modal-field">
            <label>Novo username da obra (só letras minúsculas)</label>
            <input v-model="importUsername" type="text" placeholder="Ex.: novaobra" />
          </div>
        </div>
        <p v-if="importError" class="modal-error">{{ importError }}</p>
        <p v-if="importSuccess" class="modal-success">{{ importSuccess }}</p>
        <div class="modal-actions">
          <button class="btn-admin-ghost" type="button" @click="closeImportModal">Cancelar</button>
          <button class="btn-admin-primary" type="button" :disabled="importing" @click="handleImport">
            {{ importing ? 'Importando...' : 'Importar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal-box">
        <h2 class="modal-title">Nova Obra</h2>
        <div class="modal-fields">
          <div class="modal-field">
            <label>Nome da obra</label>
            <input v-model="newSiteName" type="text" placeholder="Ex.: Obra Sul 2026" />
          </div>
          <div class="modal-field">
            <label>Username da obra (só letras minúsculas)</label>
            <input v-model="newSystemUsername" type="text" placeholder="Ex.: obrasul" />
          </div>
          <div class="modal-field">
            <label>Nome do administrador</label>
            <input v-model="newAdminName" type="text" placeholder="Ex.: Ana Souza" />
          </div>
          <div class="modal-field">
            <label>Username do administrador</label>
            <input v-model="newAdminUsername" type="text" placeholder="Ex.: ana" />
          </div>
          <div class="modal-field">
            <label>Data de nascimento do administrador</label>
            <input v-model="newAdminBirthDate" type="date" :min="MIN_BIRTH_DATE" :max="maxBirthDate()" />
          </div>
          <div class="modal-field">
            <label>Senha do administrador</label>
            <input v-model="newAdminPassword" type="password" placeholder="••••••" />
          </div>
        </div>
        <p v-if="createError" class="modal-error">{{ createError }}</p>
        <div class="modal-actions">
          <button class="btn-admin-ghost" type="button" @click="showCreateModal = false">Cancelar</button>
          <button class="btn-admin-primary" type="button" :disabled="creating" @click="createSystem">
            {{ creating ? 'Criando...' : 'Criar obra' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { masterSessionStore, masterFetch } from '@/store/masterSession'
import { birthDateError, maxBirthDate, MIN_BIRTH_DATE } from '@/utils/birthDate'
import Admin2FAModal from '@/components/admin-system/Admin2FAModal.vue'

interface SystemSummary {
  systemId: string
  systemUsername: string
  siteName: string
  primaryColor: string
  disabled: boolean
  createdAt: string
  renewalDate: string
  storageLimitMb: number
  activeUsers: number
  inactiveUsers: number
  totalUsers: number
  activeProjects: number
  usedMb: number
}

interface ReactivationRequest {
  id: string
  systemId: string
  siteName: string
  requestedByUsername: string
  requestedAt: string
  message: string
  status: string
}

interface AnalyticsData {
  totalSystems: number
  activeSystems: number
  suspendedSystems: number
  totalUsers: number
  totalProjects: number
  totalSubmissions: number
  totalFileMb: number
  expiringRenewals: { systemId: string; systemUsername: string; siteName: string; renewalDate: string }[]
  topByUsers: { systemId: string; systemUsername: string; siteName: string; userCount: number }[]
  topByActivity: { systemId: string; systemUsername: string; siteName: string; submissionCount: number }[]
}

interface StorageSystem {
  systemId: string
  siteName: string
  systemUsername: string
  usedMb: number
  limitMb: number
  pct: number
  filesMb: number
  submMb: number
  mediaMb: number
}

interface StorageOverview {
  totalUsedMb: number
  totalLimitMb: number
  totalPct: number
  filesMb: number
  submMb: number
  mediaMb: number
  systems: StorageSystem[]
}

export default defineComponent({
  name: 'AdminDashboardView',
  components: { Admin2FAModal },
  setup() {
    const router = useRouter()
    const show2fa = ref(false)
    const systems = ref<SystemSummary[]>([])
    const loading = ref(true)
    const searchQuery = ref('')
    const dashTab = ref<'ativas' | 'deletadas' | 'analytics'>('ativas')

    // Deleted systems
    interface DeletedSystem {
      systemId: string
      siteName: string
      originalSystemUsername: string
      deletedAt: string
      totalUsers: number
      totalProjects: number
      usedMb: number
    }
    const deletedSystems = ref<DeletedSystem[]>([])
    const deletedLoading = ref(false)
    const recoveringId = ref('')

    // Analytics
    const analytics = ref<AnalyticsData | null>(null)
    const analyticsLoading = ref(false)
    const analyticsSubTab = ref<'geral' | 'consumo'>('geral')

    // Storage overview
    const storageOverview = ref<StorageOverview | null>(null)
    const storageLoading = ref(false)
    const storageSortField = ref<'siteName' | 'usedMb' | 'pct' | 'limitMb'>('pct')
    const storageSortDir = ref<'asc' | 'desc'>('desc')

    const showCreateModal = ref(false)
    const newSiteName = ref('')
    const newSystemUsername = ref('')
    const newAdminName = ref('')
    const newAdminUsername = ref('')
    const newAdminPassword = ref('')
    const newAdminBirthDate = ref('')
    const createError = ref('')
    const creating = ref(false)

    // Bell / reactivation requests
    const bellOpen = ref(false)
    const reactivationRequests = ref<ReactivationRequest[]>([])
    const pendingCount = computed(() => reactivationRequests.value.length)

    const filteredSystems = computed(() => {
      const q = searchQuery.value.trim().toLowerCase()
      if (!q) return systems.value
      return systems.value.filter(s =>
        s.siteName.toLowerCase().includes(q) ||
        s.systemUsername.toLowerCase().includes(q)
      )
    })

    // KPIs agregados de todas as obras (faixa executiva no topo)
    const kpis = computed(() => {
      const list = systems.value
      const ativas = list.filter(s => !s.disabled).length
      const usedMb = list.reduce((sum, s) => sum + (s.usedMb || 0), 0)
      const limitMb = list.reduce((sum, s) => sum + (s.storageLimitMb || 0), 0)
      const usuarios = list.reduce((sum, s) => sum + (s.totalUsers || 0), 0)
      const projetos = list.reduce((sum, s) => sum + (s.activeProjects || 0), 0)
      const fmtGb = (mb: number) => mb >= 1024 ? (mb / 1024).toFixed(1) + ' GB' : Math.round(mb) + ' MB'
      return {
        total: list.length,
        ativas,
        suspensas: list.length - ativas,
        usuarios,
        projetos,
        usedLabel: fmtGb(usedMb),
        limitLabel: fmtGb(limitMb),
        storagePct: limitMb > 0 ? Math.min(100, Math.round((usedMb / limitMb) * 100)) : 0
      }
    })

    // ── Command palette (Ctrl+K / Cmd+K) ──
    const paletteOpen = ref(false)
    const paletteQuery = ref('')
    const paletteInput = ref<HTMLInputElement | null>(null)
    const paletteResults = computed(() => {
      const q = paletteQuery.value.trim().toLowerCase()
      const base = systems.value
      const list = q ? base.filter(s => s.siteName.toLowerCase().includes(q) || s.systemUsername.toLowerCase().includes(q)) : base
      return list.slice(0, 8)
    })
    const openPalette = () => {
      paletteOpen.value = true
      paletteQuery.value = ''
      nextTick(() => paletteInput.value?.focus())
    }
    const closePalette = () => { paletteOpen.value = false }
    const paletteGo = (systemId: string) => {
      paletteOpen.value = false
      router.push(`/administrador/systems/${systemId}`)
    }
    const onKeydown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        paletteOpen.value ? closePalette() : openPalette()
      } else if (e.key === 'Escape' && paletteOpen.value) {
        closePalette()
      }
    }

    const loadReactivationRequests = async () => {
      try {
        reactivationRequests.value = await masterFetch<ReactivationRequest[]>('/master/reactivation-requests')
      } catch { /* ignore */ }
    }

    const load = async () => {
      loading.value = true
      try {
        systems.value = await masterFetch<SystemSummary[]>('/master/systems/full')
      } catch { /* ignore */ } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      load()
      loadReactivationRequests()
      window.addEventListener('keydown', onKeydown)
    })
    onUnmounted(() => { window.removeEventListener('keydown', onKeydown) })

    const logout = () => {
      masterSessionStore.logout()
      router.push('/administrador')
    }

    // ── Trocar senha do master ──
    const changePwd = reactive({ open: false, current: '', next: '', confirm: '', saving: false, error: '', success: '' })
    const openChangePwd = () => {
      changePwd.open = true
      changePwd.current = ''; changePwd.next = ''; changePwd.confirm = ''
      changePwd.error = ''; changePwd.success = ''
    }
    const closeChangePwd = () => { changePwd.open = false }
    const submitChangePwd = async () => {
      changePwd.error = ''; changePwd.success = ''
      if (!changePwd.current || !changePwd.next) { changePwd.error = 'Preencha a senha atual e a nova.'; return }
      if (changePwd.next.length < 8) { changePwd.error = 'A nova senha deve ter no mínimo 8 caracteres.'; return }
      if (changePwd.next !== changePwd.confirm) { changePwd.error = 'A confirmação não confere.'; return }
      changePwd.saving = true
      try {
        await masterFetch('/master/change-password', {
          method: 'POST',
          body: JSON.stringify({ currentPassword: changePwd.current, newPassword: changePwd.next })
        })
        changePwd.success = 'Senha alterada com sucesso.'
        changePwd.current = ''; changePwd.next = ''; changePwd.confirm = ''
        setTimeout(() => { changePwd.open = false }, 1200)
      } catch (e) {
        changePwd.error = e instanceof Error ? e.message : 'Erro ao alterar a senha.'
      } finally {
        changePwd.saving = false
      }
    }

    const goToSystem = (systemId: string) => {
      router.push(`/administrador/systems/${systemId}`)
    }

    const toggleBell = () => {
      bellOpen.value = !bellOpen.value
      if (bellOpen.value) loadReactivationRequests()
    }

    const onShellClick = () => {
      bellOpen.value = false
    }

    const viewSystem = async (req: ReactivationRequest) => {
      bellOpen.value = false
      try {
        await masterFetch(`/master/reactivation-requests/${req.id}/resolve`, { method: 'PUT' })
        reactivationRequests.value = reactivationRequests.value.filter(r => r.id !== req.id)
      } catch { /* ignore */ }
      router.push(`/administrador/systems/${req.systemId}`)
    }

    const formatMb = (mb: number) => {
      if (mb >= 1024) return (mb / 1024).toFixed(2) + ' GB'
      return mb.toFixed(1) + ' MB'
    }

    const formatDate = (d: string) => {
      if (!d) return ''
      try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d }
    }

    const storageColor = (used: number, limit: number) => {
      const pct = limit > 0 ? used / limit : 0
      if (pct >= 0.9) return '#dc2626'
      if (pct >= 0.7) return '#f59e0b'
      return '#16a34a'
    }

    const createSystem = async () => {
      createError.value = ''
      if (!newSiteName.value.trim() || !newSystemUsername.value.trim() || !newAdminName.value.trim() || !newAdminUsername.value.trim() || !newAdminPassword.value.trim()) {
        createError.value = 'Preencha todos os campos.'
        return
      }
      const birthErr = birthDateError(newAdminBirthDate.value)
      if (birthErr) { createError.value = `Administrador: ${birthErr}`; return }
      creating.value = true
      try {
        await masterFetch('/master/systems', {
          method: 'POST',
          body: JSON.stringify({
            siteName: newSiteName.value.trim(),
            systemUsername: newSystemUsername.value.trim(),
            adminName: newAdminName.value.trim(),
            adminUsername: newAdminUsername.value.trim(),
            adminPassword: newAdminPassword.value.trim(),
            adminBirthDate: newAdminBirthDate.value
          })
        })
        showCreateModal.value = false
        newSiteName.value = ''
        newSystemUsername.value = ''
        newAdminName.value = ''
        newAdminUsername.value = ''
        newAdminPassword.value = ''
        newAdminBirthDate.value = ''
        await load()
      } catch (err: unknown) {
        createError.value = err instanceof Error ? err.message : 'Erro ao criar obra.'
      } finally {
        creating.value = false
      }
    }

    const loadDeleted = async () => {
      deletedLoading.value = true
      try {
        deletedSystems.value = await masterFetch<typeof deletedSystems.value>('/master/systems/deleted')
      } catch { /* ignore */ } finally {
        deletedLoading.value = false
      }
    }

    const loadAnalytics = async () => {
      if (analyticsLoading.value) return
      analyticsLoading.value = true
      try {
        analytics.value = await masterFetch<AnalyticsData>('/master/analytics')
      } catch { /* ignore */ } finally {
        analyticsLoading.value = false
      }
    }

    const loadStorageOverview = async () => {
      if (storageLoading.value) return
      storageLoading.value = true
      try {
        storageOverview.value = await masterFetch<StorageOverview>('/master/storage-overview')
      } catch { /* ignore */ } finally {
        storageLoading.value = false
      }
    }

    const storageSorted = computed(() => {
      if (!storageOverview.value) return []
      const arr = [...storageOverview.value.systems]
      arr.sort((a, b) => {
        const va = a[storageSortField.value]
        const vb = b[storageSortField.value]
        if (typeof va === 'string' && typeof vb === 'string') {
          return storageSortDir.value === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va)
        }
        return storageSortDir.value === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number)
      })
      return arr
    })

    const toggleStorageSort = (field: typeof storageSortField.value) => {
      if (storageSortField.value === field) storageSortDir.value = storageSortDir.value === 'asc' ? 'desc' : 'asc'
      else { storageSortField.value = field; storageSortDir.value = 'desc' }
    }

    const storageBarColor = (pct: number) => {
      if (pct >= 90) return '#ef4444'
      if (pct >= 70) return '#f59e0b'
      return '#22c55e'
    }

    const switchAnalyticsTab = (tab: 'geral' | 'consumo') => {
      analyticsSubTab.value = tab
      if (tab === 'consumo' && !storageOverview.value) loadStorageOverview()
    }

    const recoverSystem = async (d: { systemId: string; siteName: string }) => {
      if (!confirm(`Recuperar a obra "${d.siteName}"? Ela voltará como suspensa.`)) return
      recoveringId.value = d.systemId
      try {
        await masterFetch(`/master/systems/${d.systemId}/recover`, { method: 'PUT' })
        await loadDeleted()
        await load()
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : 'Erro ao recuperar obra.')
      } finally {
        recoveringId.value = ''
      }
    }

    // ── Export obra ──────────────────────────────────────────────────────────
    const exportLoading = ref('')
    async function exportObra(s: SystemSummary) {
      exportLoading.value = s.systemId
      try {
        const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'
        const token = masterSessionStore.getToken()
        const response = await fetch(`${apiBase}/master/systems/${encodeURIComponent(s.systemId)}/export`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store'
        })
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}))
          alert((payload as { error?: string }).error || 'Erro ao exportar obra.')
          return
        }
        const blob = await response.blob()
        const disposition = response.headers.get('Content-Disposition') || ''
        const nameMatch = disposition.match(/filename="([^"]+)"/)
        const filename = nameMatch ? nameMatch[1] : `obra-${s.systemUsername}-export.zip`
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
      } catch (e: unknown) {
        alert(e instanceof Error ? e.message : 'Erro ao exportar obra.')
      } finally {
        exportLoading.value = ''
      }
    }

    // ── Import obra ──────────────────────────────────────────────────────────
    const showImportModal = ref(false)
    const importing = ref(false)
    const importError = ref('')
    const importSuccess = ref('')
    const importUsername = ref('')
    const importFile = ref<File | null>(null)

    function onImportFile(e: Event) {
      importFile.value = (e.target as HTMLInputElement).files?.[0] ?? null
    }

    function closeImportModal() {
      showImportModal.value = false
      importError.value = ''
      importSuccess.value = ''
      importUsername.value = ''
      importFile.value = null
    }

    async function handleImport() {
      importError.value = ''
      importSuccess.value = ''
      if (!importFile.value) { importError.value = 'Selecione um arquivo ZIP.'; return }
      if (!importUsername.value.trim()) { importError.value = 'Informe o username da nova obra.'; return }
      importing.value = true
      try {
        const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'
        const token = masterSessionStore.getToken()
        const fd = new FormData()
        fd.append('file', importFile.value)
        fd.append('systemUsername', importUsername.value.trim())
        const response = await fetch(`${apiBase}/master/systems/import`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd
        })
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
          importError.value = (payload as { error?: string }).error || 'Erro ao importar obra.'
          return
        }
        importSuccess.value = `Obra "${(payload as { siteName?: string }).siteName}" importada com sucesso!`
        await load()
      } catch (e: unknown) {
        importError.value = e instanceof Error ? e.message : 'Erro ao importar obra.'
      } finally {
        importing.value = false
      }
    }

    const backupLoading = ref(false)
    async function downloadBackup() {
      backupLoading.value = true
      try {
        const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'
        const token = masterSessionStore.getToken()
        const response = await fetch(`${apiBase}/master/backup`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store'
        })
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}))
          alert((payload as { error?: string }).error || 'Erro ao gerar backup.')
          return
        }
        const blob = await response.blob()
        const disposition = response.headers.get('Content-Disposition') || ''
        const nameMatch = disposition.match(/filename="([^"]+)"/)
        const filename = nameMatch ? nameMatch[1] : `backup-${Date.now()}.db`
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
      } catch (e: unknown) {
        alert(e instanceof Error ? e.message : 'Erro ao baixar backup.')
      } finally {
        backupLoading.value = false
      }
    }

    return {
      masterSessionStore, systems, filteredSystems, loading, searchQuery,
      showCreateModal, newSiteName, newSystemUsername, newAdminName, newAdminUsername, newAdminPassword, newAdminBirthDate, MIN_BIRTH_DATE, maxBirthDate,
      createError, creating,
      bellOpen, reactivationRequests, pendingCount,
      logout, goToSystem, toggleBell, onShellClick, viewSystem,
      changePwd, openChangePwd, closeChangePwd, submitChangePwd, show2fa,
      kpis, paletteOpen, paletteQuery, paletteInput, paletteResults, openPalette, closePalette, paletteGo,
      formatMb, formatDate, storageColor, createSystem,
      dashTab, deletedSystems, deletedLoading, recoveringId, loadDeleted, recoverSystem,
      analytics, analyticsLoading, loadAnalytics,
      analyticsSubTab, storageOverview, storageLoading, storageSorted,
      storageSortField, storageSortDir, toggleStorageSort, storageBarColor, switchAnalyticsTab,
      backupLoading, downloadBackup,
      exportLoading, exportObra,
      showImportModal, importing, importError, importSuccess, importUsername,
      onImportFile, closeImportModal, handleImport
    }
  }
})
</script>

<style scoped lang="scss">
* { box-sizing: border-box; }

.admin-shell {
  min-height: 100vh;
  background: #f0f4f8;
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

/* ── Shell header ── */
.admin-nav {
  height: 48px;
  background: #0f4c81;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 0 12px;
  gap: 8px;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 0 rgba(0,0,0,0.15);
}

.admin-nav-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.admin-nav-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.admin-nav-product {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.admin-nav-divider {
  width: 1px;
  height: 16px;
  background: rgba(255,255,255,0.3);
  margin: 0 4px;
}

.admin-nav-title {
  color: rgba(255,255,255,0.75);
  font-size: 13px;
  font-weight: 400;
}

.admin-nav-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.admin-nav-user-group {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: default;
}

.admin-nav-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255,255,255,0.22);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.admin-nav-username {
  color: rgba(255,255,255,0.85);
  font-size: 13px;
  font-weight: 500;
}

.admin-nav-logout {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.85);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: rgba(255,255,255,0.2); }
}

/* ── Command bar ── */
.admin-cmdbar {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: 0 24px;
  gap: 16px;
  position: sticky;
  top: 48px;
  z-index: 90;
}

.admin-pivot {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.pivot-tab {
  background: none;
  border: none;
  border-bottom: 2.5px solid transparent;
  padding: 0 16px;
  height: 44px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s;
  white-space: nowrap;

  &:hover { color: #111827; }
  &.active {
    color: #0f4c81;
    border-bottom-color: #0f4c81;
    font-weight: 600;
  }
}

.admin-cmdbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
}

.btn-cmd {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(:disabled) { background: #f3f4f6; border-color: #9ca3af; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

.btn-cmd-primary {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #0f4c81;
  color: #fff;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #0d3d6a; }
}

.admin-content-inner {
  padding: 28px 0;
}

.admin-main {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px 40px;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}

.admin-page-title {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.admin-loading {
  text-align: center;
  color: #6b7280;
  padding: 80px 0;
  font-size: 15px;
}

.admin-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.obra-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  }

  &.disabled { opacity: 0.6; }
}

.obra-card-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.obra-card-name {
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  flex: 1;
}

.obra-card-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 20px;
  flex-shrink: 0;

  &.badge-on { background: rgba(255,255,255,0.2); color: #fff; }
  &.badge-off { background: rgba(0,0,0,0.25); color: rgba(255,255,255,0.8); }
}

.obra-card-body {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.obra-stat-row {
  display: flex;
  gap: 16px;
}

.obra-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: #f9fafb;
  border-radius: 8px;
  padding: 10px 4px;
}

.obra-stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
}

.obra-stat-label {
  font-size: 11px;
  color: #6b7280;
  text-align: center;
}

.obra-storage {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.obra-storage-bar-wrap {
  height: 6px;
  background: #e5e7eb;
  border-radius: 99px;
  overflow: hidden;
}

.obra-storage-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.3s;
}

.obra-storage-label {
  font-size: 11px;
  color: #6b7280;
  text-align: right;
}

.obra-dates {
  display: flex;
  gap: 16px;
}

.obra-date-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.obra-date-label {
  font-size: 11px;
  color: #9ca3af;
}

.obra-date-value {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
}

/* Modal buttons */
.btn-admin-primary {
  background: #0f4c81;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) { background: #0d3d6a; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.btn-admin-ghost {
  background: transparent;
  color: #374151;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;

  &:hover { background: #f3f4f6; }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: #fff;
  border-radius: 14px;
  padding: 32px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 20px;
}

.modal-fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
}

.modal-field {
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }

  input {
    padding: 9px 12px;
    border: 1.5px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    outline: none;

    &:focus { border-color: #0f4c81; }
  }
}

.modal-error {
  color: #dc2626;
  font-size: 13px;
  background: #fef2f2;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Search */
.admin-search-wrap {
  margin-bottom: 20px;
}
.admin-search {
  width: 100%;
  max-width: 360px;
  padding: 8px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus {
    border-color: #0f4c81;
    box-shadow: 0 0 0 3px rgba(15,76,129,0.1);
  }
}

/* Bell */
.bell-wrap {
  position: relative;
}
.admin-nav-bell {
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 6px;
  color: #fff;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  &:hover { background: rgba(255,255,255,0.22); }
}
.bell-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #dc2626;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 99px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}
.bell-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
  width: 320px;
  max-height: 420px;
  overflow-y: auto;
  z-index: 500;
}
.bell-dropdown-header {
  padding: 14px 16px 10px;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}
.bell-empty {
  padding: 20px 16px;
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
}
.bell-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 4px;
  &:last-child { border-bottom: none; }
}
.bell-item-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.bell-item-meta {
  font-size: 12px;
  color: #6b7280;
}
.bell-item-message {
  font-size: 13px;
  color: #374151;
  background: #f9fafb;
  border-radius: 6px;
  padding: 6px 8px;
}
.bell-item-btn {
  background: #0f4c81;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;
  margin-top: 4px;
  &:hover { background: #0d3d6a; }
}


/* Deleted table */
.deleted-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);

  th {
    background: #f9fafb;
    padding: 10px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    border-bottom: 1px solid #e5e7eb;
  }

  td {
    padding: 12px 16px;
    font-size: 14px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
    code { font-size: 13px; background: #f3f4f6; padding: 2px 6px; border-radius: 4px; }
  }

  tr:last-child td { border-bottom: none; }
}

.btn-recover {
  background: #16a34a;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: #15803d; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.obra-card-actions {
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
  margin-top: 4px;
}

.btn-obra-export {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  width: 100%;
  justify-content: center;
  transition: background 0.15s;
  &:hover:not(:disabled) { background: #e2e8f0; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.modal-success {
  color: #16a34a;
  font-size: 13px;
  margin: 0 0 4px;
}

/* Analytics */
.analytics-wrap { display: flex; flex-direction: column; gap: 24px; }

.analytics-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 14px;
}

.acard {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.acard-warn .acard-value { color: #f59e0b; }
}

.acard-value { font-size: 24px; font-weight: 700; color: #111827; }
.acard-label { font-size: 12px; color: #6b7280; }

.analytics-cols {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.analytics-panel {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  padding: 20px;
}

.analytics-panel-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 14px;
}

.rank-list { display: flex; flex-direction: column; gap: 8px; }
.rank-empty { font-size: 13px; color: #9ca3af; }

.rank-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f9fafb;
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: #e0e8f0; }

  &.rank-row-warn { background: #fef3c7; }
}

.rank-num { font-size: 12px; font-weight: 700; color: #9ca3af; min-width: 24px; }
.rank-name { flex: 1; font-size: 13px; font-weight: 600; color: #374151; }
.rank-val { font-size: 12px; color: #6b7280; white-space: nowrap; }

/* Analytics sub-tabs */
.analytics-subtabs {
  display: flex;
  gap: 2px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 20px;
}
.asubtab {
  padding: 9px 18px;
  background: transparent;
  border: none;
  border-bottom: 2.5px solid transparent;
  margin-bottom: -2px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: color .15s, border-color .15s;
  &:hover { color: #111827; }
  &.active { color: #0f4c81; border-bottom-color: #0f4c81; }
}

/* acard danger */
.acard.acard-danger .acard-value { color: #ef4444; }

/* Storage wrap */
.storage-wrap { display: flex; flex-direction: column; gap: 20px; }

.storage-global-bar-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  padding: 20px;
}

.storage-global-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 14px;
}

.storage-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.sleg {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #374151;
  &::before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
}
.sleg-files::before { background: #3b82f6; }
.sleg-subm::before  { background: #f59e0b; }
.sleg-media::before { background: #a855f7; }
.sleg-free::before  { background: #e5e7eb; }

/* Stacked bar */
.storage-stacked-bar, .mini-stacked {
  height: 12px;
  border-radius: 6px;
  background: #e5e7eb;
  display: flex;
  overflow: hidden;
}
.mini-stacked { height: 8px; border-radius: 4px; min-width: 80px; }
.ssb-seg { height: 100%; transition: width .4s; flex-shrink: 0; }
.ssb-files { background: #3b82f6; }
.ssb-subm  { background: #f59e0b; }
.ssb-media { background: #a855f7; }

/* Storage table */
.storage-table-panel { overflow: hidden; }
.storage-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.storage-table-note { font-size: 12px; color: #9ca3af; }
.storage-table-wrap { overflow-x: auto; }
.storage-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: .04em;
    padding: 6px 10px;
    border-bottom: 1.5px solid #e5e7eb;
    white-space: nowrap;
  }

  td { padding: 10px 10px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
}

.th-sort { cursor: pointer; user-select: none; &:hover { color: #374151; } }
.th-num { text-align: right; }
.sort-arrow { margin-left: 4px; opacity: 0.7; }

.storage-tr { cursor: pointer; transition: background .12s; &:hover { background: #f0f4f8; } }

.td-name { min-width: 160px; }
.td-site { display: block; font-weight: 600; color: #111827; }
.td-user { display: block; font-size: 11px; color: #9ca3af; }
.td-num { text-align: right; white-space: nowrap; color: #374151; font-variant-numeric: tabular-nums; }
.td-muted { color: #9ca3af; font-size: 12px; }
.td-bar { min-width: 100px; }

.pct-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
}

/* ── Faixa de KPIs ── */
.admin-kpis {
  display: flex; gap: 12px; flex-wrap: wrap; align-items: stretch;
  padding: 16px 32px; background: #fff; border-bottom: 1px solid #e5e7eb;
}
.kpi {
  display: flex; flex-direction: column; gap: 2px; justify-content: center;
  min-width: 90px; padding: 4px 0;
}
.kpi-value { font-size: 22px; font-weight: 800; color: #0f172a; line-height: 1.1; }
.kpi-value--ok { color: #16a34a; }
.kpi-value--warn { color: #dc2626; }
.kpi-label { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: .04em; }
.kpi--storage { min-width: 190px; gap: 5px; }
.kpi-storage-top { display: flex; align-items: baseline; gap: 6px; }
.kpi-storage-of { font-size: 11px; color: #94a3b8; }
.kpi-storage-bar { height: 6px; background: #e5e7eb; border-radius: 99px; overflow: hidden; }
.kpi-storage-fill { height: 100%; border-radius: 99px; transition: width .3s; }
.kpi-cmdk {
  margin-left: auto; align-self: center;
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 14px; border-radius: 9px; cursor: pointer;
  background: #f1f5f9; border: 1px solid #e2e8f0; color: #475569;
  font-size: 12.5px; font-weight: 600;
  &:hover { background: #e2e8f0; }
  kbd { background: #fff; border: 1px solid #cbd5e1; border-radius: 5px; padding: 1px 6px; font-size: 10.5px; font-family: inherit; color: #64748b; }
}

/* ── Command palette ── */
.pal-overlay { position: fixed; inset: 0; z-index: 700; background: rgba(15,23,42,.5); backdrop-filter: blur(2px); display: flex; align-items: flex-start; justify-content: center; padding: 12vh 20px 20px; }
.pal-box { background: #fff; border-radius: 14px; width: min(560px, 100%); box-shadow: 0 24px 70px rgba(0,0,0,.35); overflow: hidden; }
.pal-search { display: flex; align-items: center; gap: 10px; padding: 14px 18px; border-bottom: 1px solid #eef2f7; color: #94a3b8; }
.pal-search input { flex: 1; border: none; outline: none; font-size: 15px; color: #0f172a; }
.pal-kbd { background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 5px; padding: 2px 7px; font-size: 11px; color: #64748b; }
.pal-results { max-height: 340px; overflow-y: auto; padding: 6px; }
.pal-item {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 10px 12px; border: none; background: none; cursor: pointer; border-radius: 8px; text-align: left;
  &:hover { background: #f1f5f9; }
}
.pal-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.pal-name { font-size: 14px; font-weight: 600; color: #0f172a; }
.pal-user { font-size: 12px; color: #94a3b8; }
.pal-badge { margin-left: auto; font-size: 10px; font-weight: 700; color: #dc2626; background: #fef2f2; border-radius: 999px; padding: 1px 8px; }
.pal-empty { padding: 20px; text-align: center; color: #94a3b8; font-size: 13px; }

/* ── Modal trocar senha ── */
.cpw-overlay { position: fixed; inset: 0; z-index: 600; background: rgba(15,23,42,.55); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; padding: 20px; }
.cpw-modal { background: #fff; border-radius: 14px; width: min(400px, 100%); padding: 24px; box-shadow: 0 20px 60px rgba(0,0,0,.3); display: flex; flex-direction: column; gap: 12px; }
.cpw-title { font-size: 17px; font-weight: 700; color: #0f172a; margin: 0; }
.cpw-sub { font-size: 12.5px; color: #64748b; margin: 0 0 4px; }
.cpw-field { display: flex; flex-direction: column; gap: 5px; }
.cpw-field label { font-size: 12px; font-weight: 600; color: #334155; }
.cpw-field input { padding: 9px 12px; border: 1.5px solid #d1d5db; border-radius: 8px; font-size: 14px; outline: none; font-family: inherit; &:focus { border-color: #0f4c81; } }
.cpw-error { font-size: 12.5px; color: #dc2626; margin: 0; background: #fef2f2; padding: 7px 10px; border-radius: 6px; }
.cpw-success { font-size: 12.5px; color: #15803d; margin: 0; background: #f0fdf4; padding: 7px 10px; border-radius: 6px; }
.cpw-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }
.cpw-btn { padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; border: none; }
.cpw-btn--ghost { background: #f1f5f9; color: #334155; &:hover { background: #e2e8f0; } }
.cpw-btn--primary { background: #0f4c81; color: #fff; &:hover:not(:disabled) { background: #0d3d6a; } &:disabled { opacity: .6; cursor: default; } }
</style>
